<template>
    <div :class="$store.state.routeLoaded ? 'slide-up view' : 'hide'">
        <div class="ca">
            <router-link to="/ai-training" class="ca-back">&larr; Back to Train AI</router-link>
            <div v-if="denied" class="ca-empty">This page is only available to IronBook administrators.</div>
            <div v-else-if="error" class="ca-empty" role="alert">{{ error }} <button type="button" @click="loadEdition(editions[0]?.date || 'latest')">Try latest edition</button></div>
            <template v-else-if="loaded && !editions.length">
                <div class="ca-empty">No competitive analysis has been published yet. The first edition appears here after the weekly update runs.</div>
                <CompetitorRequests :api-url="$store.state.apiUrl" />
            </template>
            <div v-else-if="!loaded" class="ca-empty" role="status">Loading competitive analysis…</div>
            <template v-else-if="D">
                <a href="#market-research" class="ca-report-jump" @click.prevent="scrollToReport">Read the full research report ↓</a>
                <ResearchWorkspace :api-url="$store.state.apiUrl" />
                <div id="market-research" class="ca-report-anchor"></div>
                <div class="ca-kicker">Competitive analysis &middot; {{ D.run.label || 'Weekly update' }} &middot; {{ niceDate(D.run.date) }}</div>
                <h2 class="ca-title"><span v-html="md(D.title.before)"></span> <span class="ca-accent" v-html="md(D.title.accent)"></span><span v-html="md(D.title.after)"></span></h2>
                <p class="ca-lede" v-html="md(D.lede)"></p>
                <div v-if="!isLatest" class="ca-old">You're viewing the {{ niceDate(D.run.date) }} edition. <a href="#" @click.prevent="loadEdition(editions[0].date)">Go to the latest</a></div>

                <!-- Questions about this edition (routes/ai.ts /competitive-ask): answered from the report, changes nothing -->
                <section class="ca-ask" aria-labelledby="ca-ask-title">
                    <div class="ca-ask-head">
                        <h3 id="ca-ask-title">Ask about this report</h3>
                        <p>Answers come from the {{ niceDate(D.run.date) }} edition. Asking doesn't change the report. To have a competitor added, say "Add &lt;name&gt; as a competitor".</p>
                    </div>
                    <div v-if="chat.length" class="ca-ask-thread" aria-live="polite">
                        <div v-for="(t, i) in chat" :key="i" class="ca-ask-turn">
                            <div class="ca-ask-q">{{ t.q }}</div>
                            <div v-if="t.a" class="ca-ask-a">{{ t.a }}<div v-if="t.sections.length" class="ca-ask-src">From: {{ t.sections.join(' · ') }}</div></div>
                            <div v-else-if="t.error" class="ca-ask-err" role="alert">{{ t.error }}</div>
                            <div v-else class="ca-ask-a ca-ask-wait">Reading the report…</div>
                        </div>
                    </div>
                    <div v-else class="ca-ask-examples">
                        <button v-for="ex in askExamples" :key="ex" type="button" :disabled="asking" @click="ask(ex)">{{ ex }}</button>
                    </div>
                    <form class="ca-ask-form" @submit.prevent="ask()">
                        <label for="ca-ask-input" class="ca-ask-label">Your question</label>
                        <textarea id="ca-ask-input" v-model="askText" rows="2" maxlength="2000" placeholder="e.g. Which competitors have a mobile app?" @keydown.enter.exact.prevent="ask()"></textarea>
                        <div class="ca-ask-actions">
                            <button v-if="chat.length" type="button" class="ca-ask-clear" :disabled="asking" @click="chat = []">Start over</button>
                            <button type="submit" class="ai-btn" :disabled="asking || !askText.trim()">{{ asking ? 'Thinking…' : 'Ask' }}</button>
                        </div>
                    </form>
                </section>

                <!-- Competitors to add to the next edition (components/CompetitorRequests.vue) -->
                <CompetitorRequests ref="competitors" :api-url="$store.state.apiUrl" @open-edition="openEdition" />

                <div class="ca-tabs" role="tablist">
                    <button v-for="t in tabs" :key="t.key" type="button" role="tab" :aria-selected="tab == t.key" class="ca-tab" :class="{ 'ca-tab-on': tab == t.key }" @click="tab = t.key">{{ t.label }}</button>
                    <button type="button" class="ca-download" :disabled="downloading" :title="'Download the ' + niceDate(D.run.date) + ' edition as a PDF'" @click="downloadPdf">
                        <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M11 2h2v11.6l4.3-4.3 1.4 1.4L12 17.4l-6.7-6.7 1.4-1.4 4.3 4.3V2zM4 19h16v2H4z"/></svg>
                        {{ downloading ? 'Making PDF…' : 'Download PDF' }}
                    </button>
                </div>
                <div v-if="downloadError" class="ca-download-error" role="alert">{{ downloadError }}</div>

                <!-- This week -->
                <div v-if="tab == 'week'">
                    <h2 class="ca-h2"><span class="ca-num">This week</span>What changed since {{ niceDate(D.changes.since) }}</h2>
                    <p v-if="D.changes.intro" v-html="md(D.changes.intro)"></p>
                    <ul v-if="D.changes.items && D.changes.items.length" class="ca-changes">
                        <li v-for="(c, i) in D.changes.items" :key="i">
                            <span class="ca-kind" :class="'ca-kind-' + c.kind">{{ kindLabel[c.kind] || c.kind }}</span>
                            <span class="ca-ct" v-html="md(c.title)"></span>
                            <span class="ca-cb" v-html="md(c.body)"></span>
                            <span v-if="c.sources && c.sources.length" class="ca-cs">
                                <template v-for="(s, j) in c.sources" :key="j"><span v-if="j"> &middot; </span><a :href="sourceUrl(s.url)" target="_blank" rel="noopener">{{ s.title }}</a></template>
                            </span>
                        </li>
                    </ul>
                    <p v-else class="ca-muted"><em>No meaningful changes found this week.</em></p>
                    <h2 class="ca-h2"><span class="ca-num">Summary</span>The short version</h2>
                    <ul class="ca-summary">
                        <li v-for="(s, i) in D.summary" :key="i"><b v-html="md(s.title)"></b><span v-html="md(s.body)"></span></li>
                    </ul>
                </div>

                <!-- Full analysis -->
                <div v-if="tab == 'full'">
                    <section>
                        <h2 class="ca-h2"><span class="ca-num">{{ num('ironbook') }}</span><span v-html="md(D.ironbook.heading)"></span></h2>
                        <p v-html="md(D.ironbook.intro)"></p>
                        <div class="ca-grid">
                            <div v-for="(c, i) in D.ironbook.cards" :key="i" class="ca-card">
                                <span class="ca-tag" v-html="md(c.tag)"></span>
                                <h3 v-html="md(c.title)"></h3>
                                <p><span v-html="md(c.body)"></span> <span v-for="st in c.status || []" :key="st" class="ca-status" :class="'ca-st-' + st">{{ st.toUpperCase() }}</span></p>
                            </div>
                        </div>
                        <div v-if="D.ironbook.callout" class="ca-callout" v-html="md(D.ironbook.callout)"></div>
                    </section>

                    <section v-for="(sp, i) in D.spotlights || []" :key="'sp' + i">
                        <h2 class="ca-h2"><span class="ca-num">{{ num('spotlight' + i) }}</span><span v-html="md(sp.title)"></span></h2>
                        <template v-for="(b, j) in sp.blocks" :key="j">
                            <p v-if="b.p !== undefined" v-html="md(b.p)"></p>
                            <p v-else-if="b.h !== undefined"><strong v-html="md(b.h)"></strong></p>
                            <ul v-else-if="b.ul"><li v-for="(x, k) in b.ul" :key="k" v-html="md(x)"></li></ul>
                            <ol v-else-if="b.ol"><li v-for="(x, k) in b.ol" :key="k" v-html="md(x)"></li></ol>
                            <div v-else-if="b.callout" class="ca-callout" v-html="md(b.callout)"></div>
                        </template>
                    </section>

                    <section>
                        <h2 class="ca-h2"><span class="ca-num">{{ num('field') }}</span><span v-html="md(D.field.heading)"></span></h2>
                        <p v-html="md(D.field.intro)"></p>
                        <div class="ca-grid">
                            <div v-for="(c, i) in D.field.categories" :key="i" class="ca-card">
                                <span class="ca-tag" v-html="md(c.tag)"></span>
                                <h3 v-html="md(c.title)"></h3>
                                <p v-html="md(c.body)"></p>
                            </div>
                        </div>
                        <h3 class="ca-h3">The closest competitors</h3>
                        <div class="ca-grid">
                            <div v-for="(c, i) in D.field.closest" :key="i" class="ca-card">
                                <span class="ca-tag" v-html="md(c.tag)"></span>
                                <h3 v-html="md(c.name)"></h3>
                                <ul><li v-for="(b, k) in c.bullets" :key="k" v-html="md(b)"></li></ul>
                                <p class="ca-small"><template v-for="(l, k) in c.links" :key="k"><span v-if="k"> &middot; </span><a :href="sourceUrl(l.url)" target="_blank" rel="noopener">{{ l.title }}</a></template></p>
                            </div>
                        </div>
                    </section>

                    <section>
                        <h2 class="ca-h2"><span class="ca-num">{{ num('matrix') }}</span><span v-html="md(D.matrix.heading)"></span></h2>
                        <div class="ca-legend"><span><span class="ca-c ca-c-y">Y</span> yes</span><span><span class="ca-c ca-c-p">P</span> partial / limited</span><span><span class="ca-c ca-c-n">N</span> no</span><span><span class="ca-c ca-c-u">?</span> not verified</span></div>
                        <div class="ca-tablewrap">
                            <table class="ca-matrix">
                                <thead><tr><th>Product</th><th v-for="(col, i) in D.matrix.columns" :key="i" v-html="md(col)"></th></tr></thead>
                                <tbody>
                                    <tr v-for="(r, i) in D.matrix.rows" :key="i" :class="{ 'ca-ib': r.ironbook }">
                                        <td v-html="md(r.product)"></td>
                                        <td v-for="(v, j) in r.cells" :key="j"><span class="ca-c" :class="'ca-c-' + v">{{ cellLabel[v] }}</span></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <p class="ca-small" v-html="md(D.matrix.note)"></p>
                    </section>

                    <section v-for="key in ['apart', 'ahead']" :key="key">
                        <h2 class="ca-h2"><span class="ca-num">{{ num(key) }}</span><span v-html="md(D[key].heading)"></span></h2>
                        <p v-if="D[key].intro" v-html="md(D[key].intro)"></p>
                        <ol><li v-for="(it, i) in D[key].items" :key="i" v-html="titled(it)"></li></ol>
                    </section>

                    <section>
                        <h2 class="ca-h2"><span class="ca-num">{{ num('adding') }}</span><span v-html="md(D.adding.heading)"></span></h2>
                        <p v-html="md(D.adding.intro)"></p>
                        <div v-for="(t, i) in D.adding.tiers" :key="i" class="ca-card ca-tier" :class="'ca-tier-' + t.tier">
                            <span class="ca-tag" v-html="md(t.label)"></span>
                            <ul><li v-for="(it, k) in t.items" :key="k" v-html="titled(it)"></li></ul>
                        </div>
                    </section>

                    <section>
                        <h2 class="ca-h2"><span class="ca-num">{{ num('pricing') }}</span><span v-html="md(D.pricing.heading)"></span></h2>
                        <p v-html="md(D.pricing.intro)"></p>
                        <div class="ca-tablewrap">
                            <table class="ca-price">
                                <thead><tr><th>Product</th><th>Price</th><th>Model / notes</th></tr></thead>
                                <tbody><tr v-for="(r, i) in D.pricing.rows" :key="i"><td v-html="md(r.product)"></td><td v-html="md(r.price)"></td><td v-html="md(r.notes)"></td></tr></tbody>
                            </table>
                        </div>
                        <p v-html="md(D.pricing.outro)"></p>
                    </section>

                    <section>
                        <h2 class="ca-h2"><span class="ca-num">{{ num('risks') }}</span><span v-html="md(D.risks.heading)"></span></h2>
                        <ul><li v-for="(it, i) in D.risks.items" :key="i" v-html="titled(it)"></li></ul>
                    </section>

                    <section>
                        <h2 class="ca-h2"><span class="ca-num">{{ num('questions') }}</span><span v-html="md(D.questions.heading)"></span></h2>
                        <ol><li v-for="(q, i) in D.questions.items" :key="i" v-html="md(q)"></li></ol>
                    </section>

                    <section>
                        <h2 class="ca-h2"><span class="ca-num">{{ num('sources') }}</span><span v-html="md(D.sources.heading)"></span></h2>
                        <p class="ca-small" v-html="md(D.sources.limits)"></p>
                        <ul class="ca-sources">
                            <li v-for="(s, i) in D.sources.items" :key="i"><a v-if="s.url" :href="sourceUrl(s.url)" target="_blank" rel="noopener">{{ s.title }}</a><span v-else>{{ s.title }}</span></li>
                        </ul>
                    </section>
                    <p class="ca-footer" v-html="md(D.footer)"></p>
                </div>

                <!-- History -->
                <div v-if="tab == 'history'">
                    <h2 class="ca-h2"><span class="ca-num">History</span>Every edition</h2>
                    <div class="ca-editions">
                        <button v-for="e in editions" :key="e.date" type="button" class="ca-edition" :class="{ 'ca-edition-on': e.date == D.run.date }" @click="loadEdition(e.date)">
                            <span class="ca-edition-date">{{ niceDate(e.date) }}</span>
                            <span class="ca-edition-meta">{{ e.changes }} change{{ e.changes == 1 ? '' : 's' }}</span>
                            <span class="ca-edition-head">{{ e.headline }}</span>
                        </button>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script lang="ts">
import ResearchWorkspace from '../components/research/ResearchWorkspace.vue';
import CompetitorRequests from '../components/CompetitorRequests.vue';
import { defineComponent } from 'vue';
import axios from 'axios';
import { inlineText, niceDate, sourceUrl } from '@/competitiveText';

// IronBook's weekly competitive analysis (routes/competitive.ts). SuperAdmins only; the data
// is one JSON edition per week, written by N8Web's weekly job.
export default defineComponent({
    name: 'CompetitiveAnalysis',
    components: { ResearchWorkspace, CompetitorRequests },
    data() {
        return {
            D: null as any,
            editions: [] as any[],
            tab: 'week',
            loaded: false,
            denied: false,
            error: '',
            loadToken: 0,
            downloading: false,
            downloadError: '',
            chat: [] as any[],   // { q, a, sections, error } for this edition
            askText: '',
            asking: false,
            askExamples: [ 'What changed this week that we should act on?', 'Which competitor is closest to IronBook?', 'How does our pricing compare?' ],
            tabs: [ { key: 'week', label: 'This week' }, { key: 'full', label: 'Full analysis' }, { key: 'history', label: 'History' } ],
            kindLabel: { ironbook: 'IronBook', competitor: 'Competitor', new: 'New competitor', pricing: 'Pricing', market: 'Market', correction: 'Correction', input: 'Input' } as any,
            cellLabel: { y: 'Y', p: 'P', n: 'N', u: '?' } as any
        }
    },
    computed: {
        isLatest(): boolean {
            return !this.editions.length || !this.D || this.editions[0].date == this.D.run.date;
        },
        // Section numbers in display order (spotlights vary week to week)
        order(): string[] {
            if(!this.D) {
                return [];
            }
            return [ 'ironbook', ...(this.D.spotlights || []).map((_s: any, i: number) => 'spotlight' + i),
                'field', 'matrix', 'apart', 'ahead', 'adding', 'pricing', 'risks', 'questions', 'sources' ];
        }
    },
    async created() {
        try {
            this.editions = (await axios.get(`${this.$store.state.apiUrl}/api/competitive/editions`)).data;
            if(this.editions.length) {
                await this.loadEdition(this.$route.query.date ? '' + this.$route.query.date : this.editions[0].date);
            }
        } catch(e: any) {
            if(e?.response?.status == 403) {
                this.denied = true;
            } else {
                this.error = 'The competitive analysis could not be loaded.';
            }
        }
        this.loaded = true;
        this.$store.state.routeLoaded = true;
    },
    methods: {
        async ask(text?: string) {
            const q = (text || this.askText).trim();
            if(!q || this.asking || !this.D) return;
            const history = this.chat.filter((t: any) => t.a).map((t: any) => ({ q: t.q, a: t.a }));
            const turn: any = { q, a: '', sections: [], error: '' };
            this.chat.push(turn);
            this.askText = '';
            this.$nextTick(() => { const t = (this.$el as HTMLElement)?.querySelector?.('.ca-ask-thread'); if(t) t.scrollTop = t.scrollHeight; });
            this.asking = true;
            try {
                const resp = await axios.post(`${this.$store.state.apiUrl}/api/ai/competitive-ask`, { date: this.D.run.date, question: q, history });
                turn.a = resp.data.answer || 'No answer came back. Please try again.';
                turn.sections = resp.data.sections || [];
                if(resp.data.competitorRequest) (this.$refs.competitors as any)?.load();
            } catch(e: any) {
                const d = e?.response?.data;
                turn.error = typeof d == 'string' && d && d.length < 300 ? d : 'Couldn\'t answer that just now. Please try again.';
            }
            this.asking = false;
            this.$nextTick(() => { const t = (this.$el as HTMLElement)?.querySelector?.('.ca-ask-thread'); if(t) t.scrollTop = t.scrollHeight; });
        },
        // "Covered in the … edition" on a competitor request
        openEdition(date: string) {
            if(this.editions.some((e: any) => e.date == date)) this.loadEdition(date);
        },
        scrollToReport() { (this.$el as HTMLElement).querySelector('.ca-report-anchor')?.scrollIntoView({block:'start'}); },
        md: inlineText,
        sourceUrl,
        niceDate,
        titled(it: any): string {
            if(!it.title) {
                return inlineText(it.body);
            }
            const sep = /^[:;,.]/.test(it.body || '') ? '' : ' ';
            return `<strong>${inlineText(it.title)}</strong>${sep}${inlineText(it.body)}`;
        },
        num(key: string): string {
            const i = (this.order as string[]).indexOf(key);
            return i < 0 ? '' : String(i + 1).padStart(2, '0');
        },
        // The whole edition (This week + Full analysis) as a PDF made on the server
        async downloadPdf() {
            if(this.downloading || !this.D) return;
            this.downloading = true;
            this.downloadError = '';
            const date = this.D.run.date;
            try {
                const resp = await axios.get(`${this.$store.state.apiUrl}/api/competitive/editions/${encodeURIComponent(date)}/pdf`, { responseType: 'blob' });
                const url = URL.createObjectURL(resp.data);
                const a = document.createElement('a');
                a.href = url;
                a.download = `IronBook competitive analysis ${date}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 60000);
            } catch(e: any) {
                const msg = e?.response?.data instanceof Blob ? await e.response.data.text().catch(() => '') : '';
                this.downloadError = msg || 'The PDF could not be made — please try again.';
            }
            this.downloading = false;
        },
        async loadEdition(date: string) {
            const token = ++this.loadToken;
            this.error = '';
            try {
                const data = (await axios.get(`${this.$store.state.apiUrl}/api/competitive/editions/${encodeURIComponent(date)}`)).data;
                if (token !== this.loadToken) return;
                this.D = data;
                this.tab = 'week';
                this.chat = [];
                // The app scrolls an inner container, not the window
                this.$nextTick(() => (this.$el as HTMLElement)?.querySelector?.('.ca')?.scrollIntoView({ block: 'start' }));
            } catch(e: any) {
                if (token !== this.loadToken) return;
                this.denied = e?.response?.status === 403;
                this.error = 'That edition could not be loaded.';
            }
        }
    }
});
</script>

<style scoped>
.ca-report-jump {float:right;font-size:12px;margin-top:3px;color:#9b5229;}
@media(max-width:500px){.ca-report-jump{float:none;display:block;margin-top:12px;}}
.ca { max-width: 1100px; margin: 0 auto; padding: 20px 14px 70px; color: #2a2a2a; line-height: 1.55; }
.ca-back { display: inline-block; font-size: 13px; color: #a0958a; text-decoration: none; margin-bottom: 14px; }
.ca-empty { background: #fff; border: 1px solid #f6e3d3; border-radius: 14px; padding: 22px; color: #66625e; }
.ca-kicker { font-family: ui-monospace, Menlo, monospace; font-size: 12px; letter-spacing: .06em; text-transform: uppercase; color: #a0958a; }
.ca-title { font-weight: 900; font-size: clamp(28px, 5vw, 46px); line-height: 1.05; letter-spacing: -.02em; text-transform: uppercase; margin: 10px 0 12px; }
.ca-accent { color: #ff6e00; }
.ca-lede { font-size: 17px; color: #66625e; max-width: 66ch; }
.ca-old { background: #fff8ec; border: 1px solid #f3dcae; border-radius: 10px; padding: 10px 14px; margin: 12px 0; font-size: 14px; }
.ca-ask { margin: 20px 0 6px; background: #fff; border: 1px solid #ecdccd; border-radius: 14px; padding: 16px 18px; display: grid; gap: 12px; }
.ca-ask-head h3 { margin: 0; font-size: 17px; font-weight: 800; color: #2a2a2a; }
.ca-ask-head p { margin: 2px 0 0; font-size: 13.5px; color: #8b8682; }
.ca-ask-thread { display: grid; gap: 12px; max-height: 460px; overflow-y: auto; }
.ca-ask-turn { display: grid; gap: 6px; }
.ca-ask-q { justify-self: end; max-width: 85%; background: #fff1e5; color: #2a2a2a; border-radius: 12px 12px 2px 12px; padding: 8px 12px; font-weight: 600; white-space: pre-wrap; overflow-wrap: anywhere; }
.ca-ask-a { max-width: 92%; background: #f6f3f0; border-radius: 12px 12px 12px 2px; padding: 10px 13px; white-space: pre-wrap; overflow-wrap: anywhere; font-size: 15px; }
.ca-ask-wait { color: #8b8682; font-style: italic; }
.ca-ask-src { margin-top: 6px; font-size: 12.5px; color: #8b8682; }
.ca-ask-err { color: #a52c26; font-size: 14px; }
.ca-ask-examples { display: flex; flex-wrap: wrap; gap: 8px; }
.ca-ask-examples button { background: #fff; border: 1px solid #e3dcd5; border-radius: 999px; padding: 6px 12px; font-size: 13.5px; color: #4b4642; cursor: pointer; font-family: inherit; }
.ca-ask-examples button:hover { border-color: #ff6e00; color: #2a2a2a; }
.ca-ask-form { display: grid; gap: 8px; }
.ca-ask-label { font-size: 12px; font-weight: 700; color: #5c4636; }
.ca-ask-form textarea { width: 100%; box-sizing: border-box; height: auto; min-height: 56px; resize: vertical; border: 1px solid #e3dcd5; border-radius: 10px; padding: 9px 12px; font: inherit; font-size: 15px; margin: 0; }
.ca-ask-actions { display: flex; justify-content: flex-end; align-items: center; gap: 10px; }
.ca-ask-clear { background: none; border: 0; color: #8b8682; cursor: pointer; font: inherit; font-size: 14px; text-decoration: underline; }
.ca-tabs { display: flex; gap: 6px; border-bottom: 1px solid #e3dcd5; margin: 22px 0 6px; flex-wrap: wrap; }
.ca-tab { background: none; border: 0; border-bottom: 3px solid transparent; padding: 10px 14px; font-weight: 600; color: #8b8682; cursor: pointer; font-size: 15px; }
.ca-tab-on { color: #2a2a2a; border-bottom-color: #ff6e00; }
.ca-download { margin-left: auto; align-self: center; display: inline-flex; align-items: center; gap: 6px; background: #fff; border: 1px solid #e3dcd5; border-radius: 8px; padding: 7px 12px; font-weight: 600; font-size: 14px; color: #2a2a2a; cursor: pointer; }
.ca-download:hover { border-color: #ff6e00; color: #b84f00; }
.ca-download:disabled { opacity: .6; cursor: wait; }
.ca-download-error { background: #fdecec; border: 1px solid #f3c4c4; color: #8a1f1f; border-radius: 8px; padding: 8px 12px; margin: 8px 0; font-size: 14px; }
@media (max-width: 500px) { .ca-download { margin: 4px 0 8px; } }
.ca-h2 { color: #2a2a2a; font-weight: 800; font-size: clamp(21px, 3vw, 27px); line-height: 1.2; margin: 38px 0 10px; }
.ca-num { display: block; font-family: ui-monospace, Menlo, monospace; font-weight: 500; font-size: 12.5px; letter-spacing: .06em; text-transform: uppercase; color: #b84f00; margin-bottom: 4px; }
.ca-h3 { color: #2a2a2a; font-size: 18px; font-weight: 700; margin: 26px 0 8px; }
.ca p, .ca li { max-width: 74ch; }
.ca a { color: #b84f00; }
.ca-muted { color: #66625e; }
.ca-changes, .ca-summary { list-style: none; padding: 0; }
.ca-changes > li, .ca-summary > li { list-style: none; }
.ca-changes { display: grid; gap: 10px; }
.ca-changes li { background: #fff; border: 1px solid #ece6e0; border-radius: 10px; padding: 12px 16px; max-width: none; }
.ca-kind { font-family: ui-monospace, Menlo, monospace; font-size: 11px; letter-spacing: .04em; text-transform: uppercase; padding: 1px 6px; border-radius: 3px; margin-right: 8px; background: #efeeec; color: #8b8682; white-space: nowrap; }
.ca-kind-ironbook { background: #fff1e6; color: #b84f00; }
.ca-kind-competitor, .ca-kind-pricing { background: #fbf0d9; color: #8a5a00; }
.ca-kind-new { background: #e5f3ec; color: #1f7a4d; }
.ca-ct { font-weight: 600; }
.ca-cb { display: block; margin-top: 4px; }
.ca-cs { display: block; margin-top: 4px; font-size: 13px; color: #8b8682; }
.ca-summary { display: grid; gap: 10px; }
.ca-summary li { background: #fff; border-left: 4px solid #ff6e00; padding: 12px 16px; max-width: 74ch; }
.ca-summary b { display: block; font-size: 16px; margin-bottom: 2px; }
.ca-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(270px, 1fr)); gap: 14px; margin: 14px 0; }
.ca-card { background: #fff; border: 1px solid #ece6e0; border-radius: 10px; padding: 16px 18px; min-width: 0; font-size: 14.5px; }
.ca-card h3 { font-size: 17px; font-weight: 700; margin: 0 0 6px; }
.ca-card ul { padding-left: 1.2em; margin: 0 0 8px; }
.ca-tag { display: block; font-family: ui-monospace, Menlo, monospace; font-size: 11px; letter-spacing: .04em; text-transform: uppercase; color: #8b8682; margin-bottom: 6px; }
.ca-status { font-family: ui-monospace, Menlo, monospace; font-size: 10.5px; padding: 1px 6px; border-radius: 3px; white-space: nowrap; }
.ca-st-live { background: #e5f3ec; color: #1f7a4d; }
.ca-st-planned, .ca-st-designed { background: #fbf0d9; color: #8a5a00; }
.ca-st-separate { background: #efeeec; color: #8b8682; }
.ca-callout { background: #fff1e6; border-radius: 8px; padding: 14px 18px; margin: 14px 0; max-width: 74ch; }
.ca-legend { display: flex; flex-wrap: wrap; gap: 14px; font-size: 13px; color: #66625e; }
.ca-tablewrap { overflow-x: auto; margin: 14px 0; border: 1px solid #ece6e0; border-radius: 8px; background: #fff; }
.ca table { border-collapse: collapse; width: 100%; font-size: 13.5px; }
.ca th, .ca td { padding: 8px 9px; border-bottom: 1px solid #f0ebe6; text-align: left; vertical-align: top; }
.ca thead th { font-family: ui-monospace, Menlo, monospace; font-weight: 500; font-size: 11px; color: #8b8682; }
.ca-matrix td, .ca-matrix thead th { text-align: center; }
.ca-matrix thead th { min-width: 80px; }
.ca-matrix td:first-child, .ca-matrix th:first-child { text-align: left; position: sticky; left: 0; background: #fff; font-weight: 600; white-space: nowrap; }
.ca-matrix tr.ca-ib td { background: #fff1e6; }
.ca-matrix tr.ca-ib td:first-child { color: #b84f00; }
.ca-c { display: inline-block; min-width: 24px; font-family: ui-monospace, Menlo, monospace; font-size: 12px; padding: 2px 5px; border-radius: 3px; }
.ca-c-y { background: #e5f3ec; color: #1f7a4d; }
.ca-c-p { background: #fbf0d9; color: #8a5a00; }
.ca-c-n { background: #efeeec; color: #8b8682; }
.ca-c-u { color: #8b8682; }
.ca-price td:nth-child(2) { font-family: ui-monospace, Menlo, monospace; white-space: nowrap; }
.ca-tier { border-left: 4px solid #e3dcd5; margin: 12px 0; }
.ca-tier-now { border-left-color: #ff6e00; }
.ca-tier-next { border-left-color: #323232; }
.ca-small { font-size: 13px; color: #66625e; }
.ca-sources { columns: 2 300px; font-size: 13px; }
.ca-footer { margin-top: 40px; padding-top: 14px; border-top: 1px solid #e3dcd5; font-size: 13px; color: #8b8682; }
.ca-editions { display: grid; gap: 8px; }
.ca-edition { display: grid; grid-template-columns: 170px 100px 1fr; gap: 10px; text-align: left; background: #fff; border: 1px solid #ece6e0; border-radius: 10px; padding: 12px 16px; cursor: pointer; font-size: 14.5px; color: #2a2a2a; }
.ca-edition-on { border-color: #ff6e00; background: #fffaf5; }
.ca-edition-date { font-weight: 600; }
.ca-edition-meta { color: #8b8682; }
@media (max-width: 600px) {
    .ca-edition { grid-template-columns: 1fr; gap: 2px; }
}
</style>
